import { Color } from '@persona-id/design-tokens';
import ChevronDownIcon from '@persona-id/design-tokens/icons/ChevronDownIcon';
import { DateTime } from 'luxon';
import { SyntheticEvent } from 'react';
import { Control, Controller, UseFormRegister } from 'react-hook-form';
import { components, MenuListProps } from 'react-select';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
  NewDataSourceType,
} from 'dashboard/forms/FormNewDataSource';
import { TableNullState } from 'dashboard/layouts/TableLayout';
import {
  DataSourceStatus,
  DataSourceUniqueStatus,
  ExternalIntegrationDataSource,
  ExternalIntegrationStatus,
  GoogleSheetsFilesResponse,
  S3Config,
  S3Object,
} from 'dashboard/models/external-integration';
import {
  IMPORTER_TYPES,
  IMPORTER_TYPES_TO_DATA_SOURCE_TYPES,
  ImporterArchiveAfterCompletion,
  ImporterType,
} from 'dashboard/models/importer';
import { NewImporterFormData } from 'dashboard/views/DashboardImporterStepperView/DashboardImporterStepperView';
import ImporterTypeIcon from 'dashboard/views/DashboardImporterStepperView/ImporterTypeIcon';
import StepperCard from 'dashboard/views/DashboardImporterStepperView/StepperCard';
import StepperFormControlLabel from 'dashboard/views/DashboardImporterStepperView/StepperFormControlLabel';
import Button from 'design-system/components/Button';
import Radio from 'design-system/components/Radio';
import RadioGroup from 'design-system/components/RadioGroup';
import Spacer from 'design-system/components/Spacer/Spacer';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Paragraph } from 'design-system/components/Text';
import IconTable from 'images/icons/imports/table.png';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

import AddDataSourceModal from '../DashboardImportersView/AddDataSourceModal';

const Line = styled('hr')`
  overflow: visible; /* For IE */
  padding: 0;
  border: none;
  border-top: solid ${Color.CoreSlate200};
  text-align: center;
  border-top-width: thin;
`;

const SelectRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0px, 1fr));
  gap: 16px;
`;

const SelectSectionHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0px, 1fr));
  gap: 8px;
  margin-bottom: 24px;
`;

const ImporterTypeLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    width: 25px;
    height: 25px;
  }
`;

const Image = styled('img')`
  max-width: 250px;
`;

const RefreshButton = styled(Button)`
  width: 'button';
`;

const TableContainer = styled.div`
  max-height: 740px; /* show 12 rows */
  overflow: auto;
`;

const NewDataSourceContainer = styled.div`
  padding: 8px;
`;

const NewDataSourceButton = styled.div`
  padding: 8px;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  &:hover {
    background-color: ${Color.CorePurple100};
  }
`;

interface ConfigureTableStepProps {
  canAdvance: boolean;
  control: Control<NewImporterFormData, object>;
  register: UseFormRegister<NewImporterFormData>;
  dataSources: ExternalIntegrationDataSource[];
  datasourceId: string;
  handleNextButtonClick: () => void;
  importerType: ImporterType;
  isActive: boolean;
  onSelectedDataSourceChange: (id: string) => void;
  handleCardClick: () => void;
  addDataSource: (values: FormNewDataSourceData) => Promise<AddDataSourceResponse>;
  refreshDataSource: (dataSource: ExternalIntegrationDataSource) => Promise<void>;
  s3Config: S3Config | null;
  getAllGoogleSheets: (code: string, redirectUrl: string) => Promise<GoogleSheetsFilesResponse>;
  handleListS3Objects: (values: FormNewDataSourceDataS3) => Promise<S3Object[]>;
}
const ConfigureTableStep = ({
  canAdvance,
  control,
  register,
  handleCardClick,
  isActive,
  dataSources,
  importerType,
  datasourceId,
  handleNextButtonClick,
  // adding new data source
  onSelectedDataSourceChange,
  addDataSource,
  refreshDataSource,
  s3Config,
  getAllGoogleSheets,
  handleListS3Objects,
}: ConfigureTableStepProps) => {
  const dataSourceTypes = IMPORTER_TYPES_TO_DATA_SOURCE_TYPES[importerType];
  const isGoogleSheets = importerType === ApiModel.ImporterPipelineGoogleSheets;
  const dataSourceOptions = dataSources
    .filter((d) => {
      return (
        importerType &&
        dataSourceTypes.includes(d.type) &&
        d.attributes.status !== ExternalIntegrationStatus.Archived
      );
    })
    .sort(
      (a, b) =>
        DateTime.fromISO(b.attributes['created-at']).valueOf() -
        DateTime.fromISO(a.attributes['created-at']).valueOf(),
    )
    .map((d) => {
      return {
        label: d.attributes.name,
        value: d.id,
      };
    });

  const dataSource = datasourceId ? dataSources.find((d) => d.id === datasourceId) : null;

  const dataSourceTableOptions =
    dataSource?.attributes?.schema?.tables?.map((t) => {
      return {
        label: t.name,
        value: t.name,
      };
    }) || [];

  const dataSourceIsProcessing =
    (dataSource?.attributes?.status as DataSourceStatus) === DataSourceUniqueStatus.Processing;
  const dataSourceIsUnhealthy =
    (dataSource?.attributes?.status as DataSourceStatus) === ExternalIntegrationStatus.Unhealthy;

  const tableIsEmpty = !dataSource || dataSource?.attributes.schema?.tables.length === 0;

  const [addDataSourceModal, { enable: showAddDataSourceModal, disable: hideAddDataSourceModal }] =
    useToggle(false);

  const handleSubmitAddDataSource = async (values: FormNewDataSourceData) => {
    const response = await addDataSource(values);

    if (response && typeof response !== 'string') {
      hideAddDataSourceModal();
      onSelectedDataSourceChange(response.id);
    }
    return response;
  };

  const archiveAfterCompletionOptions: OptionType<ImporterArchiveAfterCompletion>[] = [
    { label: 'Yes, deactivate after completion.', value: ImporterArchiveAfterCompletion.True },
    { label: 'No, keep after completion.', value: ImporterArchiveAfterCompletion.False },
  ];

  return (
    <>
      <ModalWrapper isOpen={addDataSourceModal} handleCloseModal={hideAddDataSourceModal}>
        <AddDataSourceModal
          handleClose={hideAddDataSourceModal}
          handleSubmitAddDataSource={handleSubmitAddDataSource}
          getAllGoogleSheets={getAllGoogleSheets}
          s3Config={s3Config}
          handleListS3Objects={handleListS3Objects}
          dataSourceType={dataSourceTypes?.[0] as NewDataSourceType}
        />
      </ModalWrapper>
      <StepperCard
        id='importer-stepper-panel__configure-table'
        title='2. Configure table'
        actions={
          isActive ? (
            <Button
              variant='basic'
              disabled={!canAdvance}
              id='importer-stepper-panel__setup-importer__next'
              onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                e.preventDefault();
                e.stopPropagation();
                handleNextButtonClick();
              }}
            >
              Next
            </Button>
          ) : (
            <ChevronDownIcon />
          )
        }
        onClick={() => handleCardClick()}
        active={isActive}
      >
        <SelectSectionHeader>
          <ImporterTypeLabel>
            <ImporterTypeIcon type={IMPORTER_TYPES[importerType]} />
            {IMPORTER_TYPES[importerType]}
          </ImporterTypeLabel>
        </SelectSectionHeader>
        <SelectRow>
          <div>
            <StepperFormControlLabel htmlFor='dataSourceId'>
              Select a data source
            </StepperFormControlLabel>
            <Controller
              name='dataSourceId'
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <ReactSelect
                  components={{
                    MenuList: (props: MenuListProps<OptionType>) => {
                      return (
                        <>
                          <components.MenuList {...props} />
                          <Line />
                          <NewDataSourceContainer>
                            <NewDataSourceButton onClick={showAddDataSourceModal}>
                              New Data Source
                            </NewDataSourceButton>
                          </NewDataSourceContainer>
                        </>
                      );
                    },
                  }}
                  id='form-add-field__data-source-id'
                  onChange={onChange}
                  value={value}
                  aria-label='Data Source ID'
                  isClearable={false}
                  options={dataSourceOptions}
                  placeholder='Select'
                  portal={true}
                />
              )}
            />
          </div>
          {isGoogleSheets && (
            <div>
              <StepperFormControlLabel htmlFor='table-name'>Sheet</StepperFormControlLabel>
              <Controller
                name='table-name'
                control={control}
                defaultValue='none'
                render={({ field: { onChange, value } }) => (
                  <ReactSelect
                    id='form-add-field__table-name'
                    onChange={onChange}
                    value={value}
                    aria-label='Table Name'
                    isClearable={false}
                    options={dataSourceTableOptions}
                    placeholder='Select'
                    portal={true}
                  />
                )}
              />
            </div>
          )}
        </SelectRow>

        <Spacer height={4} />
        <Line />
        <Spacer height={4} />
        <TableContainer>
          <Table aria-label='Data source schema table'>
            {!tableIsEmpty && (
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Columns</TableHeaderCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {!tableIsEmpty ? (
                dataSource?.attributes.schema?.tables.map(
                  (table: { name: string; columns: { name: string }[] }) => {
                    return (
                      <tr key={table.name}>
                        <TableCell>{table.name}</TableCell>
                        <TableCell>
                          {table.columns.map((column: { name: string }) => column.name).join(', ')}
                        </TableCell>
                      </tr>
                    );
                  },
                )
              ) : (
                <TableNullState>
                  {dataSourceIsProcessing ? (
                    <LoadingIndicator />
                  ) : dataSourceIsUnhealthy ? (
                    <>
                      <Paragraph size='lg'>
                        Data source is in an unhealthy state. Cannot process table schema.
                      </Paragraph>
                      <Spacer height={2} />
                      <RefreshButton
                        onClick={() => {
                          refreshDataSource(dataSource as ExternalIntegrationDataSource);
                        }}
                      >
                        Refresh
                      </RefreshButton>
                    </>
                  ) : (
                    <>
                      {' '}
                      <Image src={IconTable} alt='Table' />
                      <Spacer height={2} />
                      <Paragraph size='lg'>
                        Select a data source and table to preview data
                      </Paragraph>
                    </>
                  )}
                </TableNullState>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Spacer height={2} />
        <StepperFormControlLabel htmlFor='archiveAfterCompletion'>
          Delete data source and Importer after use?
        </StepperFormControlLabel>
        <RadioGroup>
          {archiveAfterCompletionOptions.map((option) => {
            return (
              <Radio
                key={option.value}
                label={option.label}
                {...register('archiveAfterCompletion')}
                value={option.value}
              />
            );
          })}
        </RadioGroup>
      </StepperCard>
    </>
  );
};

export default ConfigureTableStep;
